import React from 'react';

import config from 'configs/app';
import { getTotalSupply } from 'configs/saba';
import useApiQuery from 'lib/api/useApiQuery';
import { useAddressHighlightContext } from 'lib/contexts/addressHighlight';
import { Link } from 'toolkit/chakra/link';
import SabaLayout from 'ui/saba/SabaLayout';
import { transactionDisplayLabel } from 'ui/saba/transaction/format';
import TransactionIdentity from 'ui/saba/TransactionIdentity';
import TransactionValue from 'ui/saba/TransactionValue';
import useProjects from 'ui/saba/useProjects';

import Chart from './Chart';
import DataState from './DataState';
import { balance, dateTime, number } from './format';
import Hash from './Hash';
import Icon from './Icon';
import type { IconName } from './Icon';
import styles from './SabaHome.module.css';
import useDashboard from './useDashboard';
import useTokenLeaderboard from './useTokenLeaderboard';

const queryOptions = { refetchInterval: 30_000, staleTime: 15_000, retry: 1 };
const tabs = [
  {
    id: 'transactions',
    label: 'تراکنش‌ها',
    title: 'آخرین تراکنش‌ها',
    href: '/txs',
  },
  {
    id: 'contracts',
    label: 'قراردادهای هوشمند',
    title: 'قراردادهای تأییدشده',
    href: '/verified-contracts',
  },
  {
    id: 'accounts',
    label: 'حساب‌های برتر',
    title: 'دارندگان برتر توکن‌های پروژه',
    href: '/tokens',
  },
  { id: 'blocks', label: 'بلاک‌ها', title: 'آخرین بلاک‌ها', href: '/blocks' },
] as const;
type TabId = (typeof tabs)[number]['id'];

function Metric({ label, value, unit, href }: { label: string; value: string; unit?: string; href?: string }) {
  return (
    <div className={ styles.metric }>
      <span>{ label }</span>
      <strong>
        { href ? <Link href={ href }>{ value }</Link> : value }
        { unit && <small>{ unit }</small> }
      </strong>
    </div>
  );
}

function Card({
  icon,
  label,
  children,
  loading,
  secondary,
}: {
  icon: IconName;
  label: string;
  children: React.ReactNode;
  loading?: boolean;
  secondary?: React.ReactNode;
}) {
  return (
    <section className={ styles.statCard } aria-label={ label } aria-busy={ loading }>
      <div className={ styles.statTop }>
        <span className={ styles.statIcon }>
          <Icon name={ icon } size={ 23 }/>
        </span>
        <h2>{ label }</h2>
        { secondary && <div className={ styles.statSecondary }>{ secondary }</div> }
      </div>
      <div className={ styles.statDetails }>{ children }</div>
    </section>
  );
}

function transactionStatus(status: string | null | undefined) {
  if (status === 'ok') return { label: 'موفق', className: styles.successTag };
  if (status === 'error') return { label: 'ناموفق', className: styles.errorTag };
  return { label: 'در انتظار', className: styles.tag };
}

export default function SabaHome() {
  const highlight = useAddressHighlightContext();
  const [ activeTab, setActiveTab ] = React.useState<TabId>('transactions');
  const stats = useApiQuery('general:stats', { queryOptions });
  const tokenLeaderboard = useTokenLeaderboard();
  const contracts = useApiQuery('general:verified_contracts', { queryOptions });
  const blocks = useApiQuery('general:homepage_blocks', { queryOptions });
  const transactions = useApiQuery('general:homepage_txs', {
    queryParams: { token_transfer_summary: 'true' },
    queryOptions: { ...queryOptions, enabled: activeTab === 'transactions' },
  });
  const dashboard = useDashboard();
  const projects = useProjects();
  const coin = config.chain.currency.symbol || 'SABA';
  const decimals = config.chain.currency.decimals;
  const data = stats.data;
  const latest = blocks.data?.[0];
  const previous = blocks.data?.[1];
  const tab = tabs.find((item) => item.id === activeTab)!;
  const selectedQuery = { accounts: tokenLeaderboard, contracts, transactions, blocks }[activeTab];
  const items = {
    accounts: tokenLeaderboard.data?.entries,
    contracts: contracts.data?.items,
    transactions: transactions.data,
    blocks: blocks.data,
  }[activeTab];
  const supply = getTotalSupply(config.chain.id);

  let connectionMessage = 'به‌روزرسانی خودکار';
  if (stats.isError) connectionMessage = 'ارتباط قطع است';
  if (stats.isPending) connectionMessage = 'در حال اتصال';
  const retryStats = React.useCallback(() => {
    void stats.refetch();
  }, [ stats ]);
  const handleTabClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const next = tabs.find((item) => item.id === event.currentTarget.dataset.tab);
    if (next) setActiveTab(next.id);
  }, []);
  const handleTabKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      const index = tabs.findIndex((item) => item.id === activeTab);
      let next = index;
      if (event.key === 'ArrowLeft') next = (index + 1) % tabs.length;
      else if (event.key === 'ArrowRight') next = (index + tabs.length - 1) % tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = tabs.length - 1;
      else return;
      event.preventDefault();
      setActiveTab(tabs[next].id);
      document.getElementById(`tab-${ tabs[next].id }`)?.focus();
    },
    [ activeTab ],
  );

  return (
    <SabaLayout home>
      <main id="explorer-content" className={ styles.content }>
        { stats.isError && (
          <div className={ styles.errorBanner } role="alert">
            { data ? 'به‌روزرسانی متوقف شده؛ آخرین اطلاعات دریافتی نمایش داده می‌شود.' : 'ارتباط با اکسپلورر برقرار نشد.' }
            <button type="button" onClick={ retryStats }>
              تلاش دوباره
            </button>
          </div>
        ) }
        <div className={ styles.statsGrid } id="network-statistics">
          <Card icon="wallet" label="تراکنش‌ها" loading={ stats.isPending }>
            <div className={ styles.metricRow }>
              <Metric label="کل تراکنش‌ها" value={ number(data?.total_transactions, 0) } href="/txs"/>
            </div>
            <div className={ styles.metricRow }>
              <Metric label="تراکنش‌های امروز" value={ number(dashboard.data?.daily.at(-1)?.transactions, 0) }/>
            </div>
            <div className={ styles.metricRow }>
              <Metric label="حساب‌های فعال امروز" value={ number(dashboard.data?.daily.at(-1)?.accounts, 0) }/>
            </div>
            <div className={ styles.metricRow }>
              <Metric label="کل حساب‌ها" value={ number(data?.total_addresses, 0) } href="/accounts"/>
            </div>
          </Card>
          <Card icon="coin" label={ `اطلاعات کوین ${ coin }` }>
            <div className={ styles.metricRow }>
              <Metric label="عرضه کل" value={ number(supply, 0) } unit={ coin }/>
            </div>
            <div className={ styles.metricRow }>
              <Metric label="نماد کوین" value={ coin }/>
            </div>
            <div className={ styles.metricRow }>
              <Metric label="قیمت کوین" value={ number(data?.coin_price, 4) } unit={ data?.coin_price ? 'دلار' : undefined }/>
            </div>
            <div className={ styles.metricRow }>
              <Metric
                label="ارزش بازار"
                value={ data?.coin_price ? number(data.market_cap) : '—' }
                unit={ data?.coin_price ? 'دلار' : undefined }
              />
            </div>
          </Card>
          <Card
            icon="box"
            label="بلاک‌ها"
            loading={ blocks.isPending }
            secondary={ (
              <span className={ `${ styles.liveBadge } ${ stats.isError ? styles.offlineBadge : '' }` }>
                <i/>
                { connectionMessage }
              </span>
            ) }
          >
            <div className={ styles.metricRow }>
              <Metric label="آخرین بلاک" value={ number(latest?.height, 0) } href={ latest ? `/block/${ latest.height }` : undefined }/>
            </div>
            <div className={ styles.metricRow }>
              <Metric label="زمان ثبت" value={ dateTime(latest?.timestamp, true) }/>
            </div>
            <div className={ styles.metricRow }>
              <Metric label="بلاک قبلی" value={ number(previous?.height, 0) } href={ previous ? `/block/${ previous.height }` : undefined }/>
            </div>
            <div className={ styles.metricRow }>
              <Metric label="زمان ثبت" value={ dateTime(previous?.timestamp, true) }/>
            </div>
          </Card>
        </div>

        <div className={ styles.analyticsGrid }>
          <div className={ styles.charts }>
            <Chart
              title="حساب‌های فعال روزانه"
              href="/accounts"
              description="تعداد حساب‌های یکتای فرستنده یا گیرنده در تراکنش‌های ثبت‌شدهٔ هر روز؛ شامل امروز."
              points={
                dashboard.data?.daily.map((point) => ({
                  date: point.date,
                  value: point.accounts,
                })) ?? []
              }
              loading={ dashboard.isPending }
              error={ dashboard.isError }
              retry={ dashboard.refetch }
            />
            <Chart
              title="تراکنش‌های روزانه"
              description="تعداد تراکنش‌های ثبت‌شدهٔ هر روز؛ شامل تراکنش‌های امروز."
              points={
                dashboard.data?.daily.map((point) => ({
                  date: point.date,
                  value: point.transactions,
                })) ?? []
              }
              loading={ dashboard.isPending }
              error={ dashboard.isError }
              retry={ dashboard.refetch }
            />
          </div>
          <section className={ `${ styles.panel } ${ styles.leaderboard }` } aria-label="پروژه‌های ثبت‌شده">
            <div className={ styles.panelHeading }>
              <h2>پروژه‌ها</h2>
              <Link href="/projects">
                مشاهده بیشتر <Icon name="arrow" size={ 16 }/>
              </Link>
            </div>
            <div className={ styles.leaderboardHeader }>
              <span>پروژه</span>
              <span>عرضه کل</span>
            </div>
            { projects.isPending || projects.isError || !projects.data?.projects.length ? (
              <DataState loading={ projects.isPending } error={ projects.isError } retry={ projects.refetch }>
                هنوز پروژه‌ای روی این شبکه ثبت نشده است
              </DataState>
            ) : (
              <div>
                { projects.data.projects.slice(0, 8).map((project) => (
                  <div className={ styles.leaderboardRow } key={ project.id }>
                    <Link href={ `/token/${ project.token }` } data-hash={ highlight ? project.token : undefined }
                      onMouseEnter={ highlight?.onMouseEnter } onMouseLeave={ highlight?.onMouseLeave }
                      onFocus={ highlight?.onFocus } onBlur={ highlight?.onBlur }
                    >
                      <span className={ styles.contractIcon }>
                        <Icon name="contract" size={ 20 }/>
                      </span>
                      <span dir="auto">{ project.title || project.slug }</span>
                    </Link>
                    <span>{ project.totalSupply ? balance(project.totalSupply, project.decimals ?? 18) : '—' }</span>
                  </div>
                )) }
              </div>
            ) }
            <div className={ styles.leaderboardFoot }>
              <Icon name="contract" size={ 17 }/>
              <span>پروژه‌های ثبت‌شده در قرارداد فهرست پروژه‌های صبا</span>
            </div>
          </section>
        </div>

        <section className={ styles.activity } aria-label="فعالیت شبکه">
          <div className={ styles.tabs } role="tablist" aria-label="اطلاعات شبکه">
            { tabs.map((item) => (
              <button
                type="button"
                id={ `tab-${ item.id }` }
                key={ item.id }
                role="tab"
                aria-selected={ activeTab === item.id }
                aria-controls="saba-tab-panel"
                tabIndex={ activeTab === item.id ? 0 : -1 }
                data-tab={ item.id }
                onClick={ handleTabClick }
                onKeyDown={ handleTabKeyDown }
              >
                { item.label }
              </button>
            )) }
          </div>
          <div id="saba-tab-panel" className={ styles.panel } role="tabpanel" aria-labelledby={ `tab-${ activeTab }` } tabIndex={ 0 }>
            <div className={ `${ styles.panelHeading } ${ styles.tableHeading }` }>
              <h2>
                { tab.title }
                { activeTab === 'accounts' && tokenLeaderboard.data && (
                  <small>{ number(tokenLeaderboard.data.projectTokensCount, 0) } توکن پروژه</small>
                ) }
              </h2>
              <Link href={ tab.href }>
                مشاهده همه <Icon name="arrow" size={ 16 }/>
              </Link>
            </div>
            { selectedQuery.isPending || selectedQuery.isError || !items?.length ? (
              <DataState loading={ selectedQuery.isPending } error={ selectedQuery.isError } retry={ selectedQuery.refetch }/>
            ) : (
              <div className={ styles.tableScroll }>
                { activeTab === 'accounts' && (
                  <table className={ styles.table }>
                    <thead>
                      <tr>
                        <th scope="col">رتبه</th>
                        <th scope="col">آدرس</th>
                        <th scope="col">نوع</th>
                        <th scope="col">تنوع توکن</th>
                        <th scope="col">قدرت موجودی</th>
                        <th scope="col">امتیاز دارایی</th>
                      </tr>
                    </thead>
                    <tbody>
                      { tokenLeaderboard.data?.entries.slice(0, 10).map((account, index) => (
                        <tr key={ account.address.hash }>
                          <td>{ number(index + 1, 0) }</td>
                          <td>
                            <Hash hash={ account.address.hash }/>
                          </td>
                          <td>
                            <span className={ account.address.is_contract ? styles.contractTag : styles.accountTag }>
                              { account.address.is_contract ? 'قرارداد' : 'حساب' }
                            </span>
                          </td>
                          <td title={ account.holdings.map((holding) => holding.symbol || holding.name || holding.token).join('، ') }>
                            { number(account.tokenTypesCount, 0) } از { number(account.totalProjectTokens, 0) }
                          </td>
                          <td>{ number(account.quantityScore * 100, 2) }٪</td>
                          <td className={ styles.score }>{ number(account.score, 2) }</td>
                        </tr>
                      )) }
                    </tbody>
                  </table>
                ) }
                { activeTab === 'contracts' && (
                  <table className={ styles.table }>
                    <thead>
                      <tr>
                        <th>قرارداد</th>
                        <th>آدرس</th>
                        <th>موجودی ({ coin })</th>
                        <th>تراکنش‌ها</th>
                        <th>زمان تأیید</th>
                      </tr>
                    </thead>
                    <tbody>
                      { contracts.data?.items.slice(0, 10).map((contract) => (
                        <tr key={ contract.address.hash }>
                          <td>{ contract.address.name || 'قرارداد هوشمند' }</td>
                          <td>
                            <Hash hash={ contract.address.hash }/>
                          </td>
                          <td>{ balance(contract.coin_balance, decimals) }</td>
                          <td>{ number(contract.transactions_count, 0) }</td>
                          <td>{ dateTime(contract.verified_at) }</td>
                        </tr>
                      )) }
                    </tbody>
                  </table>
                ) }
                { activeTab === 'transactions' && (
                  <table className={ styles.table }>
                    <thead>
                      <tr>
                        <th>هش تراکنش</th>
                        <th>نوع</th>
                        <th>وضعیت</th>
                        <th>فرستنده</th>
                        <th>گیرنده</th>
                        <th>مقدار</th>
                        <th>زمان</th>
                      </tr>
                    </thead>
                    <tbody>
                      { transactions.data?.slice(0, 10).map((transaction) => (
                        <tr key={ transaction.hash }>
                          <td>
                            <TransactionIdentity hash={ transaction.hash } blockNumber={ transaction.block_number }/>
                          </td>
                          <td>
                            <span className={ styles.method } title={ transaction.method || undefined }>
                              { transactionDisplayLabel(transaction) }
                            </span>
                          </td>
                          <td>
                            <span className={ transactionStatus(transaction.status).className }>
                              { transactionStatus(transaction.status).label }
                            </span>
                          </td>
                          <td>
                            <Hash hash={ transaction.from.hash }/>
                          </td>
                          <td>{ transaction.to ? <Hash hash={ transaction.to.hash }/> : 'ایجاد قرارداد' }</td>
                          <td><TransactionValue value={ transaction.value } token_transfer_summary={ transaction.token_transfer_summary }/></td>
                          <td>{ dateTime(transaction.timestamp) }</td>
                        </tr>
                      )) }
                    </tbody>
                  </table>
                ) }
                { activeTab === 'blocks' && (
                  <table className={ styles.table }>
                    <thead>
                      <tr>
                        <th>شماره بلاک</th>
                        <th>زمان ثبت</th>
                        <th>تراکنش‌ها</th>
                        <th>تولیدکننده</th>
                        <th>گس مصرف‌شده</th>
                        <th>اندازه (بایت)</th>
                      </tr>
                    </thead>
                    <tbody>
                      { blocks.data?.slice(0, 10).map((block) => (
                        <tr key={ block.hash }>
                          <td>
                            <Link className={ styles.blockLink } href={ `/block/${ block.height }` }>
                              <Icon name="box" size={ 18 }/>
                              { number(block.height, 0) }
                            </Link>
                          </td>
                          <td>{ dateTime(block.timestamp) }</td>
                          <td>{ number(block.transactions_count, 0) }</td>
                          <td>
                            <Hash hash={ block.miner.hash }/>
                          </td>
                          <td>{ number(block.gas_used, 0) }</td>
                          <td>{ number(block.size, 0) }</td>
                        </tr>
                      )) }
                    </tbody>
                  </table>
                ) }
              </div>
            ) }
            <div className={ styles.tableFoot }>
              <span>اطلاعات مستقیماً از زنجیره صبا دریافت می‌شود.</span>
              <span>
                <Icon name="refresh" size={ 13 }/>
                به‌روزرسانی هر ۳۰ ثانیه
              </span>
            </div>
          </div>
        </section>
      </main>
    </SabaLayout>
  );
}
